import type { RioTrip } from "@types";
import { mergeSelectedTrip, mergeTripSources, toTripOptions } from "@utils/rioTripOptions";
import { useTpgTrips } from "@components/shared/useTpgTrips";
import { useRioTripsPagination } from "@components/shared/useRioTripsPagination";
import { SelectOption } from "@rio-cloud/rio-uikit";
import {
  getActiveBranches,
  getActiveCategories,
  getDrivers,
  getActiveSegments,
  getActiveSubCategories,
  getActiveSuppliers,
} from "@services";
import { useGetStoredAssets, useGetStoredSelectedVehicles } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocalizedName, useT } from "@hooks";
import { getDriverName } from "@utils/driverName";
import type { Complement } from "@components/shared/ComplementList";

export interface InactiveSelectOption extends SelectOption {
  inactive?: boolean;
  rawName?: string;
}

export interface DriverSelectOption extends SelectOption {
  rioDriverId?: string;
}

export type { Complement };

export type FormState = {
  date: string;
  value: string;
  branch: string;
  segment: string;
  supplier: string;
  trip: string;
  vehicle: string;
  driver: string[];
  category: string;
  subcategory: string;
  observation: string;
  maintenanceType?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  stoppedTime?: string;
  stopLocation?: string;
  odometerKm?: string;
  volume?: string;
  fuelUnitPrice?: string;
  paymentMethod?: string;
  addOdometerPhoto?: boolean;
  invoiceGallery?: boolean;
  complements?: Complement[];
  firstFuelType?: string;
  firstFuelVolume?: string;
  firstFuelValue?: string;
  firstFuelUnitPrice?: string;
  secondFuelType?: string;
  secondFuelVolume?: string;
  secondFuelValue?: string;
  secondFuelUnitPrice?: string;
  totalVolume?: string;
  totalValue?: string;
  totalUnitPrice?: string;
  tankCapacity?: string;
  averageConsumption?: string;
  locationCoordinates?: string;
  tollLocation?: string;
  tollCoordinates?: string;
  tollLatitude?: string;
  tollLongitude?: string;
  concessionaire?: string;
  plaza?: string;
  tagType?: string;
  tagNumber?: string;
  axesCategory?: string;
  tollIsIntegration?: boolean;
  status?: string;
  dueDate?: string;
  isRecurring?: boolean;
  repeatEvery?: string;
  weekDays?: string[];
  recurrenceStart?: string;
  recurrenceEnd?: string;
  recurrenceInterval?: number;
  source?: string;
};

const initialDefaultForm = {
  date: "",
  value: "",
  branch: "",
  segment: "",
  supplier: "",
  trip: "",
  vehicle: "",
  driver: [],
  category: "",
  subcategory: "",
  observation: "",
  maintenanceType: "",
  taxes: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  stoppedTime: "",
  stopLocation: "",
  odometerKm: "",
  volume: "",
  fuelUnitPrice: "",
  paymentMethod: "",
  addOdometerPhoto: false,
  invoiceGallery: false,
  complements: [],
  firstFuelType: "",
  firstFuelVolume: "",
  firstFuelValue: "",
  firstFuelUnitPrice: "",
  secondFuelType: "",
  secondFuelVolume: "",
  secondFuelValue: "",
  secondFuelUnitPrice: "",
  totalVolume: "",
  totalValue: "",
  totalUnitPrice: "",
  tankCapacity: "",
  averageConsumption: "",
  locationCoordinates: "",
  tollLocation: "",
  tollCoordinates: "",
  tollLatitude: "",
  tollLongitude: "",
  concessionaire: "",
  plaza: "",
  tagType: "",
  tagNumber: "",
  axesCategory: "",
  tollIsIntegration: false,
  status: "",
  dueDate: "",
  isRecurring: false,
  repeatEvery: "",
  weekDays: [],
  recurrenceStart: "",
  recurrenceEnd: "",
};

export const useExpensesForm = () => {
  const [branchOptions, setBranchOptions] = useState<InactiveSelectOption[]>([]);
  const [segmentsOptions, setSegmentsOptions] = useState<InactiveSelectOption[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<InactiveSelectOption[]>([]);
  const [driversOptions, setDriversOptions] = useState<DriverSelectOption[]>([]);
  const [categoriesOptions, setCategoriesOptions] = useState<InactiveSelectOption[]>([]);
  const [vehiclesOptions, setVehiclesOptions] = useState<SelectOption[]>([]);
  const [subCategoriesOptions, setSubCategoriesOptions] = useState<InactiveSelectOption[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<RioTrip | null>(null);
  const assets = useGetStoredAssets();
  const vehiclesSelected = useGetStoredSelectedVehicles();
  const localizedName = useLocalizedName();
  const { locale } = useIntl();
  const t = useT();

  const [form, setForm] = useState<FormState>(initialDefaultForm);

  const getDefaultOptions = (type: string) => {
    const label = t("common.action.createNew");
    return [{ id: label, label, name: type }];
  };

  const mountOption = (data: any[]): InactiveSelectOption[] => {
    return data?.map((res: { id: number; name: string; pt?: string; en?: string; es?: string; active?: boolean }) => ({
      id: res.id.toString(),
      label: localizedName(res),
      inactive: res.active === false,
      rawName: res.name,
    }));
  };

  const fetchBranchOptions = async () => {
    try {
      const response = await getActiveBranches();

      setBranchOptions(mountOption(response));
    } catch (error) {
      console.log("error", error);
      setBranchOptions(getDefaultOptions("branch"));
    }
  };

  const fetchSegments = async () => {
    try {
      const response = await getActiveSegments();
      setSegmentsOptions(mountOption(response));
    } catch {
      setSegmentsOptions(getDefaultOptions("segment"));
    }
  };

  const fetchSupplier = async () => {
    try {
      const response = await getActiveSuppliers("FORNECEDOR");
      setSupplierOptions(mountOption(response));
    } catch {
      setSupplierOptions(getDefaultOptions("supplier"));
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await getDrivers();
      const options: DriverSelectOption[] = response.map((driver: Record<string, unknown>) => {
        const rioDriverId = driver.rioDriverId as string | undefined;
        return {
          id: String(driver.id as string | number),
          label: getDriverName(driver),
          rioDriverId: rioDriverId != null ? String(rioDriverId) : undefined,
        };
      });
      setDriversOptions(options);
    } catch {
      setDriversOptions([]);
    }
  };

  const { tpgTrips, addTpgTrip } = useTpgTrips();
  const {
    trips,
    hasMore: hasMoreTrips,
    isLoadingMore: isLoadingMoreTrips,
    fetchTrips,
    loadMoreTrips,
  } = useRioTripsPagination();

  const availableTrips = useMemo(
    () => mergeSelectedTrip(mergeTripSources(trips, tpgTrips), selectedTrip),
    [trips, tpgTrips, selectedTrip]
  );

  const tripsOptions = useMemo<SelectOption[]>(() => toTripOptions(availableTrips), [availableTrips]);

  const fetchVehicles = async () => {
    if (!assets) {
      return;
    }
    try {
      const filtered =
        vehiclesSelected.length > 0 ? assets?.filter((item) => vehiclesSelected?.includes(item.id)) : assets;
      setVehiclesOptions(mountOption(filtered));
    } catch {
      setVehiclesOptions([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getActiveCategories();

      const filtered = response.filter((res) => res.type === "DESPESA");

      setCategoriesOptions(mountOption(filtered));
    } catch {
      setCategoriesOptions(getDefaultOptions("category"));
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await getActiveSubCategories(form.category);
      setSubCategoriesOptions(mountOption(response.data));
    } catch {
      setSubCategoriesOptions([]);
    }
  };

  useEffect(() => {
    if (form?.category) {
      fetchSubCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.category]);

  useEffect(() => {
    fetchBranchOptions();
    fetchSegments();
    fetchSupplier();
    fetchDrivers();
    fetchCategories();
    // Refaz a busca quando o idioma muda para reaplicar os nomes localizados
    // (pt/en/es) vindos do backend, que ficavam presos no idioma da 1ª carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    fetchVehicles();
  }, [assets, vehiclesSelected]);

  return {
    segmentsOptions,
    branchOptions,
    supplierOptions,
    driversOptions,
    categoriesOptions,
    vehiclesOptions,
    subCategoriesOptions,
    tripsOptions,
    trips: availableTrips,
    selectedTrip,
    setSelectedTrip,
    tpgTrips,
    addTpgTrip,
    form,
    initialDefaultForm,
    setForm,
    fetchBranchOptions,
    fetchSegments,
    fetchSupplier,
    fetchDrivers,
    fetchVehicles,
    fetchCategories,
    fetchSubCategories,
    fetchTrips,
    loadMoreTrips,
    hasMoreTrips,
    isLoadingMoreTrips,
  };
};
